import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

export const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();

  if (cartItems.length === 0) {
    return (
      <div data-testid="cart-page" className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShoppingCart className="w-20 h-20 mx-auto mb-6 text-gray-400" />
          <h1 className="text-4xl font-bold text-[hsl(215,75%,16%)] mb-4" style={{ fontFamily: 'Outfit' }}>
            Your Cart is Empty
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Browse our shop and add items to your cart
          </p>
          <Button
            onClick={() => navigate('/shop')}
            className="btn-premium btn-premium-primary"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen bg-gray-50 py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-[hsl(215,75%,16%)] mb-8" style={{ fontFamily: 'Outfit' }}>
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl p-6 shadow-md flex items-center gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[hsl(215,75%,16%)]" style={{ fontFamily: 'Outfit' }}>
                    {item.name}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                  <p className="text-xl font-bold text-[hsl(224,64%,33%)] mt-2">
                    {item.price}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                    data-testid={`decrease-qty-${item.id}`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-bold text-lg w-8 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                    data-testid={`increase-qty-${item.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => removeFromCart(item.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  data-testid={`remove-item-${item.id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-md sticky top-28">
              <h2 className="text-2xl font-bold text-[hsl(215,75%,16%)] mb-6" style={{ fontFamily: 'Outfit' }}>
                Order Summary
              </h2>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="font-semibold">Free</span>
                </div>
                <div className="border-t pt-4 flex justify-between text-lg font-bold text-[hsl(215,75%,16%)]">
                  <span>Total</span>
                  <span>${getCartTotal().toFixed(2)}</span>
                </div>
              </div>
              <Button
                className="w-full btn-premium btn-premium-primary mb-3"
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </Button>
              <Button
                onClick={() => navigate('/shop')}
                variant="outline"
                className="w-full"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};